import React, { useState, useCallback, useMemo } from 'react'
import { Stack, Box, RadioGroup, FormControlLabel, Radio, Typography, useTheme, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import SearchFilter from './SearchFilter'
import Export from './Export'
import GraphSection from './GraphsSection'
import TableSites from './SectionSites'
import TableTypes from './SectionTypes'
import TableSsids from './SectionSsids'
import TableEvolution from './SectionEvolution'

interface BodyProps {
  onViewTypeChange?: (isEvolution: boolean) => void
}

const TableSection = ({viewType, verbatim}) => {
  return viewType === 'type'
    ? <TableTypes />
    : viewType === 'site'
      ? <TableSites viewType={viewType} verbatim={verbatim} />
      : viewType === 'ssid'
        ? <TableSsids viewType={viewType} verbatim={verbatim} />
        : <TableEvolution />
}

const Body: React.FC<BodyProps> = ({ onViewTypeChange }) => {
  const { t } = useTranslation()
  const [verbatim, setVerbatim] = useState('')
  const [viewType, setViewType] = useState<'type' | 'ssid' | 'site' | 'evolution'>('type')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const exportAction = useMemo(() => <Export />, [])

  useRegisterBreadcrumbAction(exportAction)

  const handleSearchChange = useCallback((value: string) => {
    setVerbatim(value.toLowerCase())
  }, [])

  const handleViewTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newViewType = e.target.value as 'type' | 'ssid' | 'site' | 'evolution'
    setViewType(newViewType)
    
    if (onViewTypeChange) {
      onViewTypeChange(newViewType === 'evolution')
    }
  }, [onViewTypeChange])

  return (
    <Stack spacing={2} sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
      <Box sx={{ 
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
      }}>
        <Stack 
          direction={isMobile ? 'column' : 'row'} 
          justifyContent="space-between" 
          alignItems={isMobile ? 'stretch' : 'center'} 
          spacing={isMobile ? 2 : 0}
          sx={{ zIndex: 2 }}
        >
          {viewType !== 'evolution' && (
            <Box sx={{ width: { xs: '100%', sm: '250px' }, zIndex: 2 }}>
              <SearchFilter onChange={handleSearchChange} />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Stack gap={2} direction={'row'}>
              <RadioGroup
                row
                value={viewType}
                onChange={handleViewTypeChange}
                sx={{
                  gap: 3,
                  zIndex: 2,
                  '& .MuiRadio-root': {
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '16px'
                    }
                  }
                }}
              >
                <FormControlLabel
                  value="type"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('stats.traffic.body.radio.by_type')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="site"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('stats.traffic.body.radio.by_site')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="ssid"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('stats.traffic.body.radio.by_ssid')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="evolution"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('stats.traffic.body.radio.evolution')}
                    </Typography>
                  }
                />
              </RadioGroup>
            </Stack>
          </Box>
        </Stack>
      </Box>


      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        height: 'calc(100vh - 400px)',
        minHeight: '600px',
        zIndex: 1
      }}>
        <TableSection
          viewType={viewType}
          verbatim={verbatim}
        />
        <GraphSection viewType={viewType} />

      </Box>

    </Stack>
  )
}

export default Body